import { getLogger } from '@confluence/logger';

const logger = getLogger('highlight-action');

const CREATE_TRELLO_CARD_ENDPOINT = '/wiki/api/v2/trello/inbox/card';
const CARD_NAME_MAX_LENGTH = 254;
const HORIZONTAL_ELLIPSIS = '…';
const toPayloadSafeName = (raw: string, maxLength: number = CARD_NAME_MAX_LENGTH): string => {
	const normalized = String(raw)
		.replace(/[\r\n\t]+/g, ' ')
		.trim();
	if (normalized.length <= maxLength) {
		return normalized;
	}
	const firstPartLength = Math.max(0, maxLength - HORIZONTAL_ELLIPSIS.length);
	return normalized.substring(0, firstPartLength) + HORIZONTAL_ELLIPSIS;
};

export interface CreateTrelloCardParams {
	text: string;
	contentId: string;
}

export interface CreateTrelloCardResponse {
	cardId: string;
	cardUrl: string;
	success: boolean;
}

/**
 * Creates a Trello card with the provided text content
 * @param params - The parameters for creating a Trello card
 * @returns Promise<CreateTrelloCardResponse>
 */
export const createTrelloCardHandle = async ({
	text,
	contentId,
}: CreateTrelloCardParams): Promise<CreateTrelloCardResponse | null> => {
	const payload = {
		name: toPayloadSafeName(text, CARD_NAME_MAX_LENGTH),
		description: text || 'Created via Confluence',
		sourceContentId: contentId,
		sourceUrl: typeof location !== 'undefined' ? location.href : undefined,
	};

	try {
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		const resp = await fetch(CREATE_TRELLO_CARD_ENDPOINT, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(payload),
		});

		if (!resp.ok) {
			logger.error`Failed to create Trello card for contentId=${contentId} status=${resp.status}`;
			return null;
		}

		const data = (await resp.json()) as {
			cardId?: string;
			cardUrl?: string;
		};
		const idCandidate = data.cardId;
		const urlCandidate = data.cardUrl;

		if (!idCandidate || !urlCandidate) {
			logger.error`Invalid Trello response for contentId=${contentId}`;
			return null;
		}

		const response: CreateTrelloCardResponse = {
			cardId: idCandidate || '',
			cardUrl: urlCandidate || '',
			success: true,
		};

		return response;
	} catch {
		logger.error`Error creating Trello card for contentId=${contentId}`;
		return null;
	}
};
